import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
	GeneralInformationSMSMessageValidationConst,
	MsgTypeWAOptionConst,
} from '../../../../../core/const';
import { Parameter } from '../../../../../core/models/util/api-adapter-service-models';
import { AppResponse } from '../../../../../core/models/util/api-dto/response-models';
import { ApiAdapterServiceService } from '../../../../../core/services';
import { TemplateWAModel, WhatsAppAccountDto } from '../../../../models';

@Component({
	selector: 'lib-general-information-sms',
	templateUrl: './general-information-sms.component.html',
	styleUrls: ['./general-information-sms.component.scss'],
})
export class GeneralInformationSMSComponent implements OnInit, OnDestroy {
	@Output()
	public updateGeneralInformationForm: EventEmitter<FormGroup>;

	public form: FormGroup;
	public msgTypeWAOption: any[] = [];
	public validationMessages: any;
	public accountOption: WhatsAppAccountDto[] = [];
	public isHaveNextAccountOption: boolean = true;
	public isLoadingAccountOption: boolean = false;
	public templateOption: TemplateWAModel[] = [];
	public isHaveNextTemplateOption: boolean = true;
	public isLoadingTemplateOption: boolean = false;
	public isHaveTemplateParam: boolean = false;

	private pageNoAccountOption: number = 1;
	private pageNoTemplateOption: number = 1;
	private subscribers: Subscription[] = [];

	@Input() public isReadOnlyMode: boolean;

	@Input()
	set isTouchedForm(status: boolean) {
		if (!this.updateGeneralInformationForm) {
			return;
		}

		setTimeout(() => {
			this.updateGeneralInformationForm.emit(this.form);
		});
	}

	constructor(private apiAdapterService: ApiAdapterServiceService) {
		this.updateGeneralInformationForm = new EventEmitter();
	}

	ngOnInit(): void {
		this.msgTypeWAOption = MsgTypeWAOptionConst.filter(
			t => t.code === 'TEXT' || t.code === 'TEMPLATE'
		);
		this.validationMessages = GeneralInformationSMSMessageValidationConst;

		this.initForm();
		this.initDisable();
		this.initEdit();
		this.initValidation();
		this.getAccountOptionService(
			this.getCampaignStorage?.generalInformation?.form?.medium === 'SMS'
				? this.getCampaignStorage?.generalInformation?.form?.from?.name
				: ''
		);
	}

	get getCampaignStorage(): any {
		return localStorage.getItem('telin.campaign')
			? JSON.parse(localStorage.getItem('telin.campaign'))
			: {};
	}

	private initForm(): void {
		this.form = new FormGroup({
			channel: new FormControl(null, Validators.required),
			from: new FormControl(null, Validators.required),
			text: new FormControl(null, Validators.required),
			template: new FormControl(null, Validators.required),
			paramBody: new FormArray([], Validators.required),
			isUpload: new FormControl(false),
			fileId: new FormControl(''),
		});

		this.form.valueChanges.subscribe(() => {
			setTimeout(() => {
				this.updateGeneralInformationForm.emit(this.form);
			});
		});
	}

	private initDisable(): void {
		if (!this.isReadOnlyMode) {
			return;
		}

		this.form.disable();
	}

	private initEdit(): void {
		if (
			Object.entries(this.getCampaignStorage).length === 0 ||
			this.getCampaignStorage?.generalInformation?.form?.medium !== 'SMS'
		)
			return;

		this.form
			.get('channel')
			.setValue(
				this.getCampaignStorage?.generalInformation?.form.channel
			);
		this.form
			.get('from')
			.setValue(this.getCampaignStorage?.generalInformation?.form.from);
		this.form
			.get('text')
			.setValue(this.getCampaignStorage?.generalInformation?.form.text);
		this.form
			.get('template')
			.setValue(
				this.getCampaignStorage?.generalInformation?.form.template
			);
		this.form
			.get('isUpload')
			.setValue(
				this.getCampaignStorage?.generalInformation?.form.isUpload
			);
		this.form
			.get('fileId')
			.setValue(this.getCampaignStorage?.generalInformation?.form.fileId);

		this.isHaveTemplateParam = this.form.get('isUpload').value;
		this.getTemplateOptionService(
			null,
			this.form.get('template').value?.name
		);
	}

	get getIsTemplate(): boolean {
		return (
			this.form.get('channel').value &&
			this.form.get('channel').value?.toLowerCase() === 'template'
		);
	}

	private initValidation(): void {
		for (const key in this.form.controls) {
			this.form.controls[key].clearValidators();
			this.form.controls[key].updateValueAndValidity();
		}

		this.form.get('channel').setValidators(Validators.required);
		this.form.get('from').setValidators(Validators.required);

		if (this.getIsTemplate) {
			this.form.get('template').setValidators(Validators.required);
			this.form.get('paramBody').setValidators(Validators.required);
			this.form.get('template').updateValueAndValidity();
			this.form.get('paramBody').updateValueAndValidity();
		} else {
			this.form.get('text').setValidators(Validators.required);
			this.form.get('text').updateValueAndValidity();
		}
	}

	public formReset(_isMediaType: boolean = false): void {
		this.form.get('text').reset();
		this.form.get('template').reset();
		(this.form.get('paramBody') as FormArray).controls = [];
		(this.form.get('paramBody') as FormArray).reset();

		this.initValidation();
	}

	private initParam(_name: string, _value: any, _type: string): Parameter {
		return {
			name: _name,
			value: _value,
			in: 'query',
			type: _type,
		};
	}

	private getAccountOptionService(
		filter: string = '',
		isInfinite: boolean = false
	): void {
		this.isLoadingAccountOption = true;

		const parameterList: Array<Parameter> = [
			this.initParam('pageNo', this.pageNoAccountOption, 'number'),
			this.initParam('rowPerPage', 20, 'number'),
			this.initParam('platform', 'SMS', 'string'),
			this.initParam('status', 'APPROVED', 'string'),
			this.initParam('filter', filter, 'string'),
		];

		const subs = this.apiAdapterService
			.executeApiRequest(parameterList, '/admin/account', 'GET')
			.subscribe((resp: AppResponse<WhatsAppAccountDto[]>) => {
				if (!resp.data) return;

				this.accountOption = isInfinite
					? [...this.accountOption, ...resp.data]
					: resp.data;
				this.isHaveNextAccountOption = resp.haveNext;
				this.isLoadingAccountOption = false;
			});

		this.subscribers.push(subs);
	}

	public getTemplateOptionService(
		event: string = null,
		filter: string = '',
		isInfinite: boolean = false
	): void {
		this.isLoadingTemplateOption = true;

		const parameterList: Array<Parameter> = [
			this.initParam('pageNo', this.pageNoTemplateOption, 'number'),
			this.initParam('rowPerPage', 20, 'number'),
			this.initParam('platform', 'SMS', 'string'),
			this.initParam('filter', filter, 'string'),
			this.initParam(
				'clientId',
				this.form.get('from').value?.clientId,
				'string'
			),
			this.initParam(
				'wabaId',
				this.form.get('from').value?.wabaId ?? '',
				'string'
			),
		];

		if (this.isReadOnlyMode)
			parameterList.push(this.initParam('summary', true, 'string'));

		const subs = this.apiAdapterService
			.executeApiRequest(parameterList, '/message/template', 'GET')
			.subscribe((resp: AppResponse<TemplateWAModel[]>) => {
				this.templateOption = isInfinite
					? [...this.templateOption, ...resp.data]
					: resp.data;
				this.isHaveNextTemplateOption = resp.haveNext;
				this.isLoadingTemplateOption = false;

				const template = this.templateOption?.find(
					t => t.ID === this.form.get('template').value?.ID
				);

				if (template) {
					this.form.get('template').setValue(template);

					if (this.form.get('isUpload').value)
						return this.updateDownloadCSV(
							this.form.get('isUpload').value
						);

					this.updateTemplate(this.form.get('template').value);
				}
			});

		this.subscribers.push(subs);
	}

	private initParameter(value: string = ''): FormGroup {
		const form = new FormGroup({
			name: new FormControl(
				{
					value: value,
					disabled:
						this.isReadOnlyMode ||
						['UnsubscribeAccount'].includes(value),
				},
				Validators.required
			),
		});

		return form;
	}

	get getParamBody(): FormGroup[] {
		return (this.form.get('paramBody') as FormArray)
			.controls as FormGroup[];
	}

	public addParamBody(value: string = ''): void {
		const form = this.initParameter(value);
		(this.form.get('paramBody') as FormArray).push(form);
	}

	public getNextAccountBatch() {
		if (!this.isHaveNextAccountOption) return;

		this.pageNoAccountOption += 1;
		this.getAccountOptionService('', true);
	}

	public accountSelectedDisplay(
		attribute1: WhatsAppAccountDto,
		attribute2: WhatsAppAccountDto
	): string {
		return attribute1 && attribute2 && attribute1.id == attribute2.id
			? `${attribute1.name} (${attribute1.accountNo})`
			: '';
	}

	public getNextTemplateBatch() {
		if (!this.isHaveNextTemplateOption) return;

		this.pageNoTemplateOption += 1;
		this.getTemplateOptionService(null, '', true);
	}

	public templateSelectedDisplay(
		attribute1: TemplateWAModel,
		attribute2: TemplateWAModel
	): string {
		return attribute1 && attribute2 && attribute1.name == attribute2.name
			? `${attribute1.name}`
			: '';
	}

	public updateTemplate(template: TemplateWAModel): void {
		if (!template) return;

		this.isHaveTemplateParam = template.body.examples?.length > 0;

		template.body.examples?.length > 0
			? this.form.get('paramBody').setValidators(Validators.required)
			: this.form.get('paramBody').clearValidators();

		(this.form.get('paramBody') as FormArray).controls = [];
		(this.form.get('paramBody') as FormArray).reset();
		this.form.get('paramBody').updateValueAndValidity();

		const formStorage = this.getCampaignStorage?.generalInformation?.form;

		template.body.examples?.forEach((each, index) => {
			let paramValue =
				formStorage?.paramBody?.[index]?.name ??
				formStorage?.paramBody?.[index] ??
				'';

			if (['UnsubscribeAccount'].includes(each)) paramValue = each;

			this.addParamBody(paramValue);
		});
	}

	public updateSearch(searchValue: string): void {
		this.pageNoTemplateOption = 1;
		this.getTemplateOptionService('', searchValue);
	}

	public updateSearchAccount(searchValue: string): void {
		this.pageNoAccountOption = 1;
		this.getAccountOptionService(searchValue);
	}

	public updateDownloadCSV(isChecked: boolean): void {
		this.form.get('isUpload').setValue(isChecked);

		if (isChecked) {
			(this.form.get('paramBody') as FormArray).controls = [];
			(this.form.get('paramBody') as FormArray).reset();
			this.form.get('paramBody').clearValidators();
			this.form.get('paramBody').updateValueAndValidity();
			return;
		}

		this.updateTemplate(this.form.get('template').value);
	}

	ngOnDestroy(): void {
		this.subscribers.forEach(each => each.unsubscribe());
	}
}
